/**
 * Detects question type and inline answer options from a parsed Question label item.
 *
 * `parseLabelsAndLists` (utils/labelUtils.ts) only captures the first line of a
 * question (`Q LABEL: question text...`) into `LabelItem.originalLines`. To
 * determine the question type and any inline options, we need to scan the
 * questionnaire text downward from the question's line number until the next
 * structural element.
 */

import { LabelItem, LabelType } from "utils/labelUtils"
import { QuestionType } from "./grammar"

export interface QuestionInfo {
  type: QuestionType
  /** True if the question's list/options are ordered (range list or numeric). */
  isOrdinal: boolean
  /** True if the question is a grid (uses PER). */
  isGrid: boolean
  /**
   * Inline answer options written directly under the question.
   * Each entry is the raw line text (with prefix punctuation stripped where unambiguous).
   */
  inlineOptions: string[]
  /** Name of the LIST or RANGE LIST referenced via FROM (if any). */
  fromListName?: string
  /** Name of the LIST referenced via PER (grid rows). */
  perListName?: string
  /**
   * Underlying SELECT cardinality, preserved even when `type` collapses to
   * HIDDEN/GRID. Used by the RECODE wizard to decide which recode modes a
   * HIDDEN target supports (e.g. SELECT_N requires a multi-select target).
   */
  selectKind?: "ONE" | "MULTIPLE"
  /**
   * Underlying FILL IN kind, preserved even when `type` collapses to HIDDEN.
   * Used by the RECODE wizard to offer arithmetic on HIDDEN fill-in targets.
   */
  fillKind?: QuestionType
}

const STOP_PREFIXES = [
  "Q ", "SECTION", "LIST ", "RANGE LIST", "RANDOMIZE", "NOTE ", "TEXT ",
  "EXCEPTIONS", "HIDDEN", "DATA ", "BEGIN", "END", "IF ", "FOR EACH",
  "TERMINATE", "SKIP TO", "VALIDATE", "HIDE", "INSERT AT", "INSERT AFTER",
  "EXTEND", "TRIM", "ADJUST LIST", "ADD LIST", "REDEFINE LIST",
  "VARIANT", "EMBED", "CALCULATE", "LANGUAGE", "LOCATION", "RECODE",
] as const

function isStopLine(line: string): boolean {
  const t = line.trim()
  if (!t) return false
  if (t.startsWith("#")) return false // comments don't end a question block
  // Match `Q ` and other multi-word starts; exclude lines that begin with a
  // capital word but are actually answer options.
  return STOP_PREFIXES.some((p) => t === p.trim() || t.startsWith(p))
}

/**
 * Get the lines belonging to a question, starting from its declaration.
 * `questionLine` is 1-based (LabelItem.lineNumbers[0]).
 */
function getQuestionBlock(
  allLines: string[],
  questionLine: number,
): string[] {
  const startIdx = questionLine - 1
  if (startIdx < 0 || startIdx >= allLines.length) return []
  const block: string[] = [allLines[startIdx]]
  for (let i = startIdx + 1; i < allLines.length; i++) {
    if (isStopLine(allLines[i])) break
    block.push(allLines[i])
  }
  return block
}

/** Strip a leading `[[...]]` modifier-block from a SELECT line for type detection. */
function stripLeadingDecorators(line: string): string {
  return line.replace(/\[\[[^\]]*\]\]/g, "").trim()
}

/**
 * Try to parse "SELECT ONE|MULTIPLE|MANY [FROM <listName>]" and similar variants.
 * Returns null if no SELECT statement is found in the block.
 */
function parseSelect(
  block: string[],
): {
  type: QuestionType
  fromListName?: string
  perListName?: string
  isGrid: boolean
} | null {
  // Find the SELECT line (may not be the first; question text can wrap).
  for (const raw of block) {
    const line = stripLeadingDecorators(raw)
    const upper = line.toUpperCase()

    // SELECT FROM RANGE [a..b]
    if (/^SELECT\s+FROM\s+RANGE/i.test(line)) {
      return { type: QuestionType.SELECT_RANGE, isGrid: false }
    }

    // SELECT ONE | SELECT MULTIPLE | SELECT MANY  (optionally FROM <list>)
    // Trailing FROM with no list name (e.g. "SELECT ONE FROM" + inline options) is allowed.
    const m = line.match(
      /^SELECT\s+(ONE|MULTIPLE|MANY)(?:\s+FROM(?:\s+(.+))?)?$/i,
    )
    if (m) {
      const sel = m[1].toUpperCase()
      const rest = (m[2] ?? "").trim()
      const fromListName = rest ? extractListName(rest) : undefined
      const type =
        sel === "ONE"
          ? QuestionType.SELECT_ONE
          : QuestionType.SELECT_MULTIPLE
      return { type, fromListName, isGrid: false }
    }
    if (upper.startsWith("SELECT ")) {
      // Generic fallback: assume single-select if we couldn't categorise.
      return { type: QuestionType.SELECT_ONE, isGrid: false }
    }
  }
  return null
}

/**
 * The FROM target may be a list name with spaces, optionally followed by
 * filtering pragmas (e.g. `Tracked_Suppliers IF SELECTED AWAREFUND`,
 * `Tracked_Suppliers | Other (please specify) + None of these`).
 * Take everything before the first `|`, `IF SELECTED`, `IF NOT SELECTED`, or `+`.
 */
function extractListName(rest: string): string {
  const stops = [
    rest.indexOf("|"),
    rest.search(/\bIF\s+SELECTED\b/i),
    rest.search(/\bIF\s+NOT\s+SELECTED\b/i),
    rest.indexOf("+"),
  ].filter((n) => n >= 0)
  const cut = stops.length ? Math.min(...stops) : rest.length
  return rest.slice(0, cut).trim()
}

/** Extract the PER <listName> directive if present (signals a grid). */
function findPer(block: string[]): string | undefined {
  for (const raw of block) {
    const line = raw.trim()
    if (/^PER\s*$/i.test(line)) {
      // Bare PER means "PER block" — list items follow inline. No name.
      return undefined
    }
    const m = line.match(/^PER\s+(.+)$/i)
    if (m) return extractListName(m[1])
  }
  return undefined
}

/** True if the question block contains a `PER` clause (grid). */
function hasPer(block: string[]): boolean {
  return block.some((l) => /^PER(\s|$)/i.test(l.trim()))
}

/** Detect FILL IN NUMBER / REAL / TEXT. */
function parseFillIn(block: string[]): QuestionType | null {
  for (const raw of block) {
    const line = raw.trim().toUpperCase()
    if (/^FILL\s+IN\s+NUMBER\b/.test(line)) return QuestionType.FILL_IN_NUMBER
    if (/^FILL\s+IN\s+REAL\b/.test(line)) return QuestionType.FILL_IN_REAL
    if (/^FILL\s+IN\s+TEXT\b/.test(line)) return QuestionType.FILL_IN_TEXT
    if (/^FILL\s+IN\b/.test(line)) return QuestionType.FILL_IN_TEXT // generic FILL IN
    if (/^FILLIN\s+NUMBER\b/.test(line)) return QuestionType.FILL_IN_NUMBER
    if (/^FILLIN\s+REAL\b/.test(line)) return QuestionType.FILL_IN_REAL
    if (/^FILLIN\s+TEXT\b/.test(line)) return QuestionType.FILL_IN_TEXT
    if (/^FILLIN\b/.test(line)) return QuestionType.FILL_IN_TEXT
  }
  return null
}

/**
 * Extract inline answer-option lines: lines after the SELECT statement that
 * are not part of FROM/PER references and not other directives. Strips group
 * tags `[[group: ...]]` and the `[[open:N]]` markers.
 */
function extractInlineOptions(block: string[]): string[] {
  const opts: string[] = []
  let inOptions = false
  for (const raw of block) {
    const trimmed = raw.trim()
    if (!trimmed) continue
    if (trimmed.startsWith("#")) continue
    if (trimmed.startsWith("Q ")) continue

    const upper = trimmed.toUpperCase()
    // Start collecting after SELECT or PER block keyword
    if (
      upper.startsWith("SELECT ONE") ||
      upper.startsWith("SELECT MULTIPLE") ||
      upper.startsWith("SELECT MANY") ||
      upper.startsWith("SELECT FROM") ||
      upper === "PER"
    ) {
      inOptions = true
      // If the SELECT line references a list (FROM followed by a list name —
      // not just a trailing `FROM` followed by [[...]] decorators), don't
      // collect as inline. Strip decorators first so `SELECT MULTIPLE FROM [[RANDOMIZE]]`
      // is recognised as a bare FROM (inline options follow on next lines).
      const stripped = stripLeadingDecorators(trimmed)
      if (/\bFROM\s+\S/i.test(stripped)) {
        inOptions = false
      }
      continue
    }
    if (upper.startsWith("FILL IN") || upper.startsWith("FILLIN")) {
      inOptions = false
      continue
    }
    if (!inOptions) continue
    if (
      upper.startsWith("FROM ") ||
      upper.startsWith("PER ") ||
      upper.startsWith("RANDOMIZE") ||
      upper.startsWith("RANDOMISE") ||
      upper.startsWith("[[")
    ) {
      continue
    }
    // It's an option — strip group/open metadata
    const cleaned = trimmed
      .replace(/\[\[[^\]]*\]\]/g, "")
      .trim()
    // Options may use `value | label` syntax (e.g. `35 TO 47 | Mid Accumulators`).
    // The value before the `|` is the answer option — the exported category and
    // what RECODE/IF reference; the text after it is a descriptive label. Keep
    // the value so wizard options match the generated/parsed statements.
    const pipeIdx = cleaned.indexOf("|")
    const value = pipeIdx >= 0 ? cleaned.slice(0, pipeIdx).trim() : cleaned
    if (value) opts.push(value)
  }
  return opts
}

/** True if every option starts with a digit followed by `:` or whitespace (numeric scale). */
function looksOrdinal(options: string[]): boolean {
  if (options.length === 0) return false
  return options.every((o) => /^[+-]?\d/.test(o))
}

/**
 * Detect a HIDDEN question. `LabelItem`s for hidden questions are recorded
 * with the `Q LABEL:` parser already (see labelUtils.ts) — we infer HIDDEN
 * by checking for a `HIDDEN` token on the question's first line.
 */
function isHiddenLine(line: string): boolean {
  return /^HIDDEN\b/i.test(line.trim())
}

/**
 * Main entry point: classify a Question label.
 */
export function detectQuestionInfo(
  questionLabel: LabelItem,
  allLines: string[],
  knownLists: ReadonlyMap<string, LabelItem>,
): QuestionInfo {
  if (questionLabel.type !== LabelType.QUESTION) {
    return {
      type: QuestionType.UNKNOWN,
      isOrdinal: false,
      isGrid: false,
      inlineOptions: [],
    }
  }

  const lineNo = questionLabel.lineNumbers[0]
  const block = getQuestionBlock(allLines, lineNo)
  const isHidden = block.some(isHiddenLine)
  const isGrid = hasPer(block)

  // FILL IN takes precedence (no answer options).
  const fillType = parseFillIn(block)
  if (fillType) {
    return {
      type: isHidden ? QuestionType.HIDDEN : fillType,
      isOrdinal: false,
      isGrid: false,
      inlineOptions: [],
      fillKind: fillType,
    }
  }

  const sel = parseSelect(block)
  if (sel) {
    const inlineOptions = sel.fromListName ? [] : extractInlineOptions(block)
    const perListName = isGrid ? findPer(block) : undefined

    let isOrdinal = false
    if (sel.fromListName) {
      const listLabel = knownLists.get(sel.fromListName)
      if (listLabel?.type === LabelType.RANGE_LIST) isOrdinal = true
      else if (listLabel) {
        // Inspect list members (skip first line which is `LIST <name>`)
        const memberLines = listLabel.originalLines
          .slice(1)
          .map((l) => l.trim())
          .filter(Boolean)
        if (memberLines.length > 0) isOrdinal = looksOrdinal(memberLines)
      }
    } else if (sel.type === QuestionType.SELECT_RANGE) {
      isOrdinal = true
    } else if (inlineOptions.length > 0) {
      isOrdinal = looksOrdinal(inlineOptions)
    }

    let resolvedType = sel.type
    if (isGrid) resolvedType = QuestionType.GRID
    if (isHidden) resolvedType = QuestionType.HIDDEN

    return {
      type: resolvedType,
      isOrdinal,
      isGrid,
      inlineOptions,
      fromListName: sel.fromListName,
      perListName,
      // Preserve cardinality before the HIDDEN/GRID collapse above.
      selectKind: sel.type === QuestionType.SELECT_MULTIPLE ? "MULTIPLE" : "ONE",
    }
  }

  // No SELECT, no FILL IN — could be a HIDDEN-with-implicit-type or an unknown shape.
  if (isHidden) {
    return {
      type: QuestionType.HIDDEN,
      isOrdinal: false,
      isGrid: false,
      inlineOptions: [],
    }
  }
  return {
    type: QuestionType.UNKNOWN,
    isOrdinal: false,
    isGrid: false,
    inlineOptions: [],
  }
}

/**
 * Build a lookup map: question label → QuestionInfo. Pass once over all
 * Question/Hidden LabelItems in the questionnaire.
 */
export function buildQuestionInfoMap(
  labels: LabelItem[],
  questionnaireText: string,
): Map<string, QuestionInfo> {
  const lines = questionnaireText.split("\n")
  const listMap = new Map<string, LabelItem>()
  for (const l of labels) {
    if (l.type === LabelType.LIST || l.type === LabelType.RANGE_LIST) {
      listMap.set(l.name, l)
    }
  }
  const result = new Map<string, QuestionInfo>()
  for (const l of labels) {
    if (l.type !== LabelType.QUESTION) continue
    result.set(l.name, detectQuestionInfo(l, lines, listMap))
  }
  return result
}

/**
 * Resolve the answer-option universe for a given question. Combines inline
 * options with the contents of any FROM list. Useful for the value picker.
 */
export function resolveAnswerOptions(
  info: QuestionInfo,
  knownLists: ReadonlyMap<string, LabelItem>,
): string[] {
  if (info.inlineOptions.length > 0) return info.inlineOptions
  if (!info.fromListName) return []
  const list = knownLists.get(info.fromListName)
  if (!list) return []
  // Skip the first line (`LIST <name>` or `RANGE LIST <name>:`)
  return list.originalLines
    .slice(1)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      // Strip group/open tags and `value | label` punctuation.
      const cleaned = l.replace(/\[\[[^\]]*\]\]/g, "").trim()
      if (info.type === QuestionType.SELECT_RANGE) {
        // RANGE LIST entries look like `1: Strongly disagree` — keep as-is.
        return cleaned
      }
      // Keep the value before the `|` (the answer option), not the label after.
      const pipeIdx = cleaned.indexOf("|")
      return pipeIdx >= 0 ? cleaned.slice(0, pipeIdx).trim() : cleaned
    })
}
